import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { formatDate, today } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { type IDTRole, idtRoles } from '@seen/core/models/care-teams/constants'
import { IDT } from '@seen/core/models/care-teams/idt'
import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { ROLES } from '@seen/core/roles'
import { now } from '@seen/core/utils'
import { Button, Label } from '@seen/ui'
import { parseISO } from 'date-fns'
import { type JSX, useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { RolePractitionerSelect } from '#src/components/care-team/RolePractitionerSelect.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const memberFieldsSchema = z.object(
  Object.fromEntries(
    idtRoles.map((role) => [role, z.string().nullable()])
  ) as Record<IDTRole, z.ZodNullable<z.ZodString>>
)

const careTeamFormSchema = z.object({
  effectiveDate: z.string().min(1, 'Effective date is required'),
  members: memberFieldsSchema,
})

// Converts the picked date into the membership transition instant. A date of
// today means "now" so same-day changes are immediately active.
function toEffectiveInstant(effectiveDate: string): string {
  return effectiveDate === today()
    ? now()
    : parseISO(effectiveDate).toISOString()
}

export function CareTeamEditDialog({
  participant,
  idt,
  open,
  onOpenChange,
}: {
  participant: Participant
  idt: IDT | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}): JSX.Element {
  const { data: practitioners } = useSearch(
    ExtendedPractitioner,
    ExtendedPractitioner.queryByIDTRoles(idtRoles)
  )
  const referencesByString = useMemo(
    () =>
      new Map(
        (practitioners ?? []).map((practitioner) => [
          practitioner.reference.reference,
          practitioner.reference,
        ])
      ),
    [practitioners]
  )

  const currentMembers = useMemo(
    () =>
      Object.fromEntries(
        idtRoles.map((role) => [
          role,
          idt?.getMemberByRole(role)?.practitioner ?? undefined,
        ])
      ) as Record<IDTRole, Reference<Practitioner> | undefined>,
    [idt]
  )

  const defaultValues = useMemo(
    () => ({
      effectiveDate: today(),
      members: Object.fromEntries(
        idtRoles.map((role) => [role, currentMembers[role]?.reference ?? null])
      ) as Record<IDTRole, string | null>,
    }),
    [currentMembers]
  )

  const form = useForm({
    schema: careTeamFormSchema,
    defaultValues,
  })

  // Reset only on the closed→open transition: defaultValues gets a new
  // reference on every background IDT refetch, and resetting then would wipe
  // in-progress edits while the dialog is open.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(defaultValues)
    }
    wasOpenRef.current = open
  }, [open, defaultValues, form])

  const patchIdt = usePatchMutation<IDT>()
  // Failures surface through the catch in handleSubmit instead.
  const createIdt = useCreateMutation<IDT>({ disableDefaultErrorToast: true })
  const isSaving = patchIdt.isPending || createIdt.isPending

  const members = form.watch('members')
  const effectiveDate = form.watch('effectiveDate')
  const changedRoles = idtRoles.filter(
    (role) =>
      members[role] &&
      members[role] !== (currentMembers[role]?.reference ?? null)
  )

  const handleSubmit = form.handleSubmit(async (values) => {
    const asOf = toEffectiveInstant(values.effectiveDate)
    const changes = changedRoles.flatMap((role) => {
      const reference = referencesByString.get(values.members[role] ?? '')
      return reference ? [{ role, reference }] : []
    })

    if (changes.length === 0) {
      onOpenChange(false)
      return
    }

    try {
      if (idt) {
        await patchIdt.mutateAsync({
          instance: idt,
          mutateFn: (team) => {
            for (const change of changes) {
              team.setMemberByRole(change.role, change.reference, { asOf })
            }
          },
        })
      } else {
        await createIdt.mutateAsync(
          new IDT({
            members: changes.map((change) => ({
              practitioner: change.reference,
              role: change.role,
              period: { start: asOf },
            })),
            participant: participant.reference,
          })
        )
      }
    } catch (error) {
      toast.error(
        `Failed to update care team: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
      return
    }
    toast.success(
      `Care team updated (${changes.length} ${changes.length === 1 ? 'change' : 'changes'})`
    )
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <div
          data-fake-focus-trap="true"
          // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on dialog open so no visible field shows a focus ring; Tab moves on to the real first focusable child
          tabIndex={0}
          className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
        />
        <DialogHeader className="py-4 text-lg">
          <DialogTitle>Edit Care Team</DialogTitle>
        </DialogHeader>
        <DialogContent className="space-y-4 pt-0">
          <Form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="effectiveDate"
                render={({ field }) => (
                  <div className="space-y-1.5">
                    <Label>Effective date</Label>
                    <div>
                      <DatePicker
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? today())}
                        onBlur={field.onBlur}
                      />
                    </div>
                    <p className="text-xs text-foreground-secondary">
                      Changes take effect on this date — pick a past date to
                      backdate or a future date to schedule ahead.
                    </p>
                  </div>
                )}
              />
              <div className="space-y-3">
                {idtRoles.map((role) => {
                  const replaced = changedRoles.includes(role)
                    ? currentMembers[role]
                    : undefined
                  return (
                    <FormField
                      key={role}
                      control={form.control}
                      name={`members.${role}`}
                      render={({ field }) => (
                        <div className="grid grid-cols-[10rem_1fr] items-center gap-2">
                          <Label className="text-foreground-secondary">
                            {displayFromCodeableConcept(ROLES[role])}
                          </Label>
                          <div className="space-y-1">
                            <RolePractitionerSelect
                              role={role}
                              value={
                                field.value
                                  ? (referencesByString.get(field.value) ?? {
                                      reference: field.value,
                                      display: currentMembers[role]?.display,
                                    })
                                  : undefined
                              }
                              onChange={(reference) =>
                                field.onChange(reference.reference ?? null)
                              }
                              className="w-full"
                            />
                            {changedRoles.includes(role) && (
                              <p className="text-xs text-amber-700">
                                {referenceDisplay(replaced)
                                  ? `Replaces ${referenceDisplay(replaced)}`
                                  : 'New assignment'}{' '}
                                effective {formatDate(effectiveDate)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  )
                })}
              </div>
            </div>
          </Form>
        </DialogContent>
        <DialogFooter className="sm:flex-row-reverse sm:justify-start">
          <Button
            onClick={handleSubmit}
            disabled={isSaving || changedRoles.length === 0}
          >
            {isSaving
              ? 'Saving…'
              : changedRoles.length > 0
                ? `Save ${changedRoles.length} ${changedRoles.length === 1 ? 'change' : 'changes'}`
                : 'Save'}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
